// src/pages/PatientVisitSummaryPage.jsx
// Route: /patient/visits/:visitId
//
// One signed visit in plain language: what the doctor found, vitals, medicines,
// next steps, advice and the prescription PDF. All shaping (hiding empty
// sections, flag -> badge, lab/follow-up detection) is in utils/visitSummary.js;
// this file only renders the view-model.
//
// API: GET /api/patients/{patientId}/visits/{visitId}
//      GET /api/patients/{patientId}/prescriptions/{id}/pdf  (blob)

import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { PatientAppShell } from '../components/PatientLayout';
import PageHead from '../components/PageHead';
import EmptyState from '../components/EmptyState';
import Icon from '../components/Icon';
import { usePatient } from '../context/PatientContext';
import patientVisitApi from '../services/patientVisitApi';
import { buildVisitSummaryModel, markVisitSeen } from '../utils/visitSummary';
import { LAB_TEST_ORDER_PATH } from '../utils/labTestSuggestion';

const BOOK_VISIT_PATH = '/patient/appointments/book';
const TONE_CLASS = { alert: 'badge-high', watch: 'badge-moderate', ok: 'badge-active' };

function Section({ title, icon, children, id }) {
  return (
    <section className="card visit-section" aria-labelledby={id}>
      <h2 className="card-title" id={id}>
        {icon && <span className="card-title-icon"><Icon name={icon} size={16} /></span>}
        {title}
      </h2>
      {children}
    </section>
  );
}

function VitalsCard({ vitals }) {
  return (
    <Section id="visit-vitals" title="Your vitals" icon="heart">
      <div className="visit-tiles">
        {vitals.tiles.map(t => (
          <div className="visit-tile" key={t.key}>
            <span className="visit-tile__label">{t.label}</span>
            <span className="visit-tile__value">
              {t.value}{t.unit && <span className="visit-tile__unit">{t.unit}</span>}
            </span>
            {t.badge && <span className={`badge ${TONE_CLASS[t.badge.tone] || 'badge-unknown'}`}>{t.badge.label}</span>}
          </div>
        ))}
      </div>
      <p className="visit-subtle">
        {vitals.measuredOn ? `Measured on ${vitals.measuredOn}. ` : ''}
        {vitals.anyFlagged
          ? 'Ask your doctor if you are unsure what a reading means for you.'
          : 'Readings are for information only.'}
      </p>
    </Section>
  );
}

function MedicinesCard({ rx, allergyWarning, onView, onDownload, busy, pdfError }) {
  return (
    <Section id="visit-medicines" title="Your medicines" icon="pill">
      {allergyWarning && (
        <div className="alert alert-warning" role="note">
          <Icon name="alert" size={16} />
          <span>Allergy on your record: {allergyWarning}. Tell the pharmacist before you buy any medicine.</span>
        </div>
      )}
      <ul className="visit-list">
        {rx.items.map((m, i) => (
          <li key={`${m.name}-${i}`}>
            <div className="visit-item__name">{m.name}</div>
            <div className="visit-item__detail">
              {[m.dose, m.timing, m.duration].filter(Boolean).join(' · ')}
            </div>
            {m.instructions && <div className="visit-item__detail">{m.instructions}</div>}
          </li>
        ))}
      </ul>
      {rx.id && (
        <>
          <p className="visit-subtle">
            {rx.rxNumber ? `Prescription ${rx.rxNumber}` : 'Your prescription'}
          </p>
          <div className="visit-actions" style={{ marginTop: 10 }}>
            <button type="button" className="btn btn-secondary btn-sm" disabled={busy} onClick={onView}>
              <Icon name="document" size={14} /> View prescription
            </button>
            <button type="button" className="btn btn-ghost btn-sm" disabled={busy} onClick={onDownload}>
              <Icon name="download" size={14} /> Download PDF
            </button>
          </div>
          {pdfError && <div className="alert alert-error" role="alert" style={{ marginTop: 10, marginBottom: 0 }}>{pdfError}</div>}
        </>
      )}
    </Section>
  );
}

function NextStepsCard({ steps, followUp }) {
  const navigate = useNavigate();
  return (
    <Section id="visit-next" title="Next steps" icon="clipboard">
      <ul className="visit-list">
        {steps.map(s => (
          <li key={s.id} className={s.completed ? 'visit-item--done' : undefined}>
            <div className="visit-item__row">
              <div>
                <div className="visit-item__name">{s.text}</div>
                {s.dueOn && <div className="visit-item__detail">By {s.dueOn}</div>}
              </div>
              {!s.completed && s.kind === 'labs' && (
                <button type="button" className="btn btn-accent btn-sm" onClick={() => navigate(LAB_TEST_ORDER_PATH)}>
                  Order tests
                </button>
              )}
              {!s.completed && s.kind === 'followup' && (
                <button type="button" className="btn btn-accent btn-sm" onClick={() => navigate(BOOK_VISIT_PATH)}>
                  Book review visit
                </button>
              )}
            </div>
          </li>
        ))}
        {followUp && (
          <li>
            <div className="visit-item__row">
              <div className="visit-item__name">{followUp.label}</div>
              <button type="button" className="btn btn-accent btn-sm" onClick={() => navigate(BOOK_VISIT_PATH)}>
                Book review visit
              </button>
            </div>
          </li>
        )}
      </ul>
    </Section>
  );
}

export default function PatientVisitSummaryPage() {
  const { visitId } = useParams();
  const { patient } = usePatient();
  const navigate = useNavigate();
  const [state, setState] = useState({ status: 'loading', vm: null, code: null });
  const [attempt, setAttempt] = useState(0);
  const [pdfBusy, setPdfBusy] = useState(false);
  const [pdfError, setPdfError] = useState(null);

  useEffect(() => {
    if (!patient?.id) return undefined;
    let cancelled = false;
    setState({ status: 'loading', vm: null, code: null });
    patientVisitApi.get(patient.id, visitId)
      .then(data => {
        if (cancelled) return;
        const vm = buildVisitSummaryModel(data);
        if (!vm) { setState({ status: 'error', vm: null, code: null }); return; }
        markVisitSeen(patient.id, visitId);
        setState({ status: 'ready', vm, code: null });
      })
      .catch(err => { if (!cancelled) setState({ status: 'error', vm: null, code: err?.response?.status || null }); });
    return () => { cancelled = true; };
  }, [patient?.id, visitId, attempt]);

  // Opens the tab synchronously (inside the click) so popup blockers allow it,
  // then points it at the PDF once the blob arrives.
  async function openPdf(mode) {
    const rxId = state.vm?.prescription?.id;
    if (!rxId || pdfBusy) return;
    setPdfError(null);
    setPdfBusy(true);
    const tab = mode === 'view' ? window.open('', '_blank') : null;
    try {
      const blob = await patientVisitApi.prescriptionPdf(patient.id, rxId);
      const url = URL.createObjectURL(new Blob([blob], { type: 'application/pdf' }));
      if (mode === 'view' && tab) {
        tab.location.href = url;
      } else {
        const a = document.createElement('a');
        a.href = url;
        if (mode === 'download') a.download = `Prescription-${state.vm.prescription.rxNumber || 'previu'}.pdf`;
        else a.target = '_blank';
        document.body.appendChild(a);
        a.click();
        a.remove();
      }
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch {
      if (tab) tab.close();
      setPdfError('We couldn’t open your prescription. Please try again.');
    } finally {
      setPdfBusy(false);
    }
  }

  const { status, vm, code } = state;
  const crumbs = [{ label: 'Visit summaries', to: '/patient/visits' }, { label: vm?.dateLabel || 'Visit' }];

  return (
    <PatientAppShell breadcrumbs={crumbs}>
      <div className="page-shell">
        <p style={{ margin: '0 0 12px' }}>
          <Link to="/patient/visits" className="btn btn-ghost btn-sm">&larr; All visit summaries</Link>
        </p>

        {status === 'loading' && (
          <div className="skeleton-list" role="status" aria-label="Loading your visit summary">
            <div className="skeleton skeleton--hero" />
            <div className="skeleton skeleton--row" />
            <div className="skeleton skeleton--row" />
          </div>
        )}

        {status === 'error' && (code === 404 ? (
          <EmptyState
            framed
            icon="document"
            title="We couldn’t find this visit summary"
            message="It may not be ready yet, or the link may be old. Your doctor’s summary appears once the visit is finished."
            action={{ label: 'See all visit summaries', onClick: () => navigate('/patient/visits') }}
          />
        ) : (
          <div className="alert alert-error" role="alert">
            <span>We couldn&rsquo;t load this visit summary.</span>
            <button type="button" className="btn btn-ghost btn-sm" onClick={() => setAttempt(n => n + 1)}>
              Try again
            </button>
          </div>
        ))}

        {status === 'ready' && (
          <>
            <PageHead eyebrow={vm.dateLabel} title={`Visit with ${vm.doctor}`} subtitle={[vm.specialty, vm.clinic].filter(Boolean).join(' · ') || undefined} />

            {vm.show.diagnosis && (
              <Section id="visit-found" title="What the doctor found" icon="stethoscope">
                <p className="visit-text">{vm.diagnosis}</p>
              </Section>
            )}

            {vm.show.vitals && <VitalsCard vitals={vm.vitals} />}

            {vm.show.prescription && (
              <MedicinesCard
                rx={vm.prescription}
                allergyWarning={vm.allergyWarning}
                busy={pdfBusy}
                pdfError={pdfError}
                onView={() => openPdf('view')}
                onDownload={() => openPdf('download')}
              />
            )}

            {vm.show.nextSteps && <NextStepsCard steps={vm.steps} followUp={vm.followUp} />}

            {vm.show.advice && (
              <Section id="visit-advice" title="Your doctor’s advice" icon="note">
                <p className="visit-text" style={{ whiteSpace: 'pre-line' }}>{vm.advice}</p>
              </Section>
            )}

            {!vm.show.diagnosis && !vm.show.vitals && !vm.show.prescription && !vm.show.nextSteps && !vm.show.advice && (
              <EmptyState compact icon="document" title="No details were added to this visit" />
            )}
          </>
        )}
      </div>
    </PatientAppShell>
  );
}
